import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronDown,
  CreditCard,
  Eye,
  Landmark,
  MonitorCog,
  ReceiptText,
  RefreshCcw,
  Search,
  Wallet,
  WalletCards,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";

import { GlobalContext } from "../../../Context/GlobalContext";
import useIntegration from "../../../services/Settings/useIntegration";
import { usePosOrderSettings } from "../../../services/Pos/usePosOrderSettings";
import LoadingScreen from "../../../utils/LoadingScreen";

const formatCurrency = (value) => {
  const amount = Number(value || 0);

  return amount.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (value) => {
  if (!value || value === "1900-01-01T00:00:00") return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTodayDate = () => {
  return new Date().toISOString().slice(0, 10);
};

const getMonthStartDate = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const getDateValue = (value) => {
  if (!value || value === "1900-01-01T00:00:00") return 0;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return 0;

  return date.getTime();
};

const getStoredNumericUserId = () => {
  try {
    const user = JSON.parse(
      sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
    );

    // userRole example: "129|Cash Manager"
    if (user?.userRole) {
      return String(user.userRole).split("|")[0]?.trim() || "";
    }

    return "";
  } catch (error) {
    console.error("Invalid user storage data", error);
    return "";
  }
};

const Payments = () => {
  const { userID } = useContext(GlobalContext);

  const { getPosTerminal, getPosDrawer } = useIntegration();

  const {
    drawerSessions,
    drawerLedger,
    paymentsLoading,
    ledgerLoading,
    getDrawerAllSessions,
    getDrawerLedgerBySessionId,
    clearDrawerLedger,
    clearDrawerSessions,
  } = usePosOrderSettings();

  const [terminals, setTerminals] = useState([]);
  const [drawers, setDrawers] = useState([]);

  const [terminalLoading, setTerminalLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [selectedTerminalId, setSelectedTerminalId] = useState("");
  const [selectedDrawerId, setSelectedDrawerId] = useState("");
  const [selectedSession, setSelectedSession] = useState(null);

  const [fromDate, setFromDate] = useState(getMonthStartDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [search, setSearch] = useState("");
  const [ledgerSearch, setLedgerSearch] = useState("");

  const numericUserId = useMemo(() => {
  return getStoredNumericUserId();
}, []);

  const selectedTerminal = useMemo(() => {
    return terminals.find(
      (terminal) =>
        String(terminal.pk_terminal_id) === String(selectedTerminalId)
    );
  }, [terminals, selectedTerminalId]);

  const selectedDrawer = useMemo(() => {
    return drawers.find(
      (drawer) => String(drawer.pk_drawer_id) === String(selectedDrawerId)
    );
  }, [drawers, selectedDrawerId]);

  const paymentSummary = useMemo(() => {
    return drawerSessions.reduce(
      (acc, session) => {
        acc.sessions += 1;
        acc.openingCash += Number(session.opening_cash || 0);
        acc.closingCash += Number(session.closing_cash || 0);
        acc.expectedCash += Number(session.expected_cash || 0);
        acc.cashDifference += Number(session.cash_difference || 0);
        acc.cashSales += Number(session.total_sales_cash || 0);
        acc.cardSales += Number(session.total_sales_card || 0);
        acc.upiSales += Number(session.total_sales_upi || 0);
        acc.refund += Number(session.total_refund || 0);
        acc.cashIn += Number(session.total_cash_in || 0);
        acc.cashOut += Number(session.total_cash_out || 0);

        return acc;
      },
      {
        sessions: 0,
        openingCash: 0,
        closingCash: 0,
        expectedCash: 0,
        cashDifference: 0,
        cashSales: 0,
        cardSales: 0,
        upiSales: 0,
        refund: 0,
        cashIn: 0,
        cashOut: 0,
      }
    );
  }, [drawerSessions]);

 const filteredSessions = useMemo(() => {
  const value = search.toLowerCase().trim();

  const searchedSessions = !value
    ? drawerSessions
    : drawerSessions.filter((session) => {
        return (
          String(session.session_code || "").toLowerCase().includes(value) ||
          String(session.session_status || "").toLowerCase().includes(value) ||
          String(session.opening_note || "").toLowerCase().includes(value) ||
          String(session.closing_note || "").toLowerCase().includes(value)
        );
      });

  return [...searchedSessions].sort((a, b) => {
    return (
      getDateValue(b.opening_datetime || b.created_on) -
      getDateValue(a.opening_datetime || a.created_on)
    );
  });
}, [drawerSessions, search]);

const filteredLedger = useMemo(() => {
  const value = ledgerSearch.toLowerCase().trim();

  const searchedLedger = !value
    ? drawerLedger
    : drawerLedger.filter((ledger) => {
        return (
          String(ledger.transaction_type || "").toLowerCase().includes(value) ||
          String(ledger.entry_mode || "").toLowerCase().includes(value) ||
          String(ledger.reference_type || "").toLowerCase().includes(value) ||
          String(ledger.remarks || "").toLowerCase().includes(value) ||
          String(ledger.amount || "").toLowerCase().includes(value) ||
          String(ledger.balance_after || "").toLowerCase().includes(value)
        );
      });

  return [...searchedLedger].sort((a, b) => {
    return (
      getDateValue(b.transaction_datetime || b.created_on) -
      getDateValue(a.transaction_datetime || a.created_on)
    );
  });
}, [drawerLedger, ledgerSearch]);

  const fetchTerminals = async () => {
    try {
      setTerminalLoading(true);

      const res = await getPosTerminal({
        uscode: userID,
      });

      if (res?.responseCode === "00") {
        const terminalList = res?.response || [];
        setTerminals(terminalList);

        if (terminalList.length > 0) {
          setSelectedTerminalId(String(terminalList[0].pk_terminal_id));
        }
      } else {
        setTerminals([]);
        toast.error(res?.responseMessage || "Failed to fetch terminals");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching terminals");
    } finally {
      setTerminalLoading(false);
    }
  };

  const fetchDrawersByTerminalId = async (terminalId) => {
    try {
      if (!terminalId) {
        setDrawers([]);
        setSelectedDrawerId("");
        return;
      }

      setDrawerLoading(true);
      setDrawers([]);
      setSelectedDrawerId("");
      setSelectedSession(null);
      clearDrawerSessions();

      const res = await getPosDrawer({
        uscode: userID,
        terminalid: String(terminalId),
      });

      if (res?.responseCode === "00") {
        const drawerList = res?.response || [];
        setDrawers(drawerList);

        if (drawerList?.length > 0) {
          setSelectedDrawerId(String(drawerList[0].pk_drawer_id));
        }
      } else {
        setDrawers([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching drawers");
      setDrawers([]);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleFetchPayments = async () => {
    if (!selectedTerminalId) {
      return toast.warning("Please select terminal");
    }

    if (!selectedDrawerId) {
      return toast.warning("Please select drawer");
    }

    if (!fromDate || !toDate) {
      return toast.warning("Please select date range");
    }

    if (!numericUserId) {
      return toast.warning("Numeric userid is missing");
    }

    setSelectedSession(null);
    clearDrawerLedger();

    await getDrawerAllSessions({
      uscode: userID,
      userid: numericUserId,
      terminalId: String(selectedTerminalId),
      drawerId: String(selectedDrawerId),
      fromDate,
      toDate,
    });
  };

const handleViewLedger = async (session) => {
  if (!session?.pk_session_id) return;

  setSelectedSession(session);
  setLedgerSearch("");

  await getDrawerLedgerBySessionId({
    sessionId: String(session.pk_session_id),
  });
};

  useEffect(() => {
    if (userID) {
      fetchTerminals();
    }
  }, [userID]);

  useEffect(() => {
    if (selectedTerminalId) {
      fetchDrawersByTerminalId(selectedTerminalId);
    }
  }, [selectedTerminalId]);

  return (
    <>
<ToastContainer/>
      <div className="p-6 h-full min-h-0 flex flex-col overflow-hidden montserrat-font-1">
        {/* HEADER */}
        <div className="mb-5 bg-white p-5 rounded-xl border shadow-sm">
          <div className="sticky top-0 z-30 bg-white pb-5">
          <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-5">
              <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                <Wallet className="w-6 h-6 text-green-600" />
                POS Payments
              </h1>

              <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
                View drawer sessions, payment totals, cash differences and
                session-wise ledger entries.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-green-50 border border-green-100 px-4 py-3 rounded-xl">
                <p className="text-xs text-gray-500">Sessions</p>
                <p className="text-xl font-semibold text-green-700">
                  {paymentSummary.sessions}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-100 px-4 py-3 rounded-xl">
                <p className="text-xs text-gray-500">Cash Sales</p>
                <p className="text-xl font-semibold text-blue-700">
                  {formatCurrency(paymentSummary.cashSales)}
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-100 px-4 py-3 rounded-xl">
                <p className="text-xs text-gray-500">UPI Sales</p>
                <p className="text-xl font-semibold text-purple-700">
                  {formatCurrency(paymentSummary.upiSales)}
                </p>
              </div>

              <div className="bg-orange-50 border border-orange-100 px-4 py-3 rounded-xl">
                <p className="text-xs text-gray-500">Difference</p>
                <p className="text-xl font-semibold text-orange-700">
                  {formatCurrency(paymentSummary.cashDifference)}
                </p>
              </div>
            </div>
          </div>
          </div>

<div className="flex-1 min-h-0 overflow-hidden">
        {/* FILTERS */}
        <div className="sticky top-0 z-20 pb-5">
        <div className="bg-white border rounded-xl shadow-sm p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                Terminal
              </label>

              <div className="relative">
                <select
                  value={selectedTerminalId}
                  onChange={(e) => setSelectedTerminalId(e.target.value)}
                  disabled={terminalLoading}
                  className="w-full border rounded-xl px-3 py-2.5 text-sm outline-none bg-white appearance-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-100"
                >
                  <option value="">
                    {terminalLoading ? "Loading terminals..." : "Select terminal"}
                  </option>

                  {terminals?.map((terminal) => (
                    <option
                      key={terminal.pk_terminal_id}
                      value={terminal.pk_terminal_id}
                    >
                      {terminal.terminal_name || "Terminal"} - #
                      {terminal.pk_terminal_id}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={16}
                  className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Drawer</label>

              <div className="relative">
                <select
                  value={selectedDrawerId}
                  onChange={(e) => {
                    setSelectedDrawerId(e.target.value);
                    setSelectedSession(null);
                    clearDrawerSessions();
                  }}
                  disabled={!selectedTerminalId || drawerLoading}
                  className="w-full border rounded-xl px-3 py-2.5 text-sm outline-none bg-white appearance-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-100"
                >
                  <option value="">
                    {drawerLoading ? "Loading drawers..." : "Select drawer"}
                  </option>

                  {drawers?.map((drawer) => (
                    <option key={drawer.pk_drawer_id} value={drawer.pk_drawer_id}>
                      {drawer.drawer_name || "Drawer"} - #{drawer.pk_drawer_id}
                    </option>
                  ))}
                </select>

                <ChevronDown
                  size={16}
                  className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                From Date
              </label>

              <div className="relative">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-400"
                />

                <CalendarDays
                  size={15}
                  className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">To Date</label>

              <div className="relative">
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-green-400"
                />

                <CalendarDays
                  size={15}
                  className="absolute right-3 top-3 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Search</label>

              <div className="flex items-center border rounded-xl px-3 py-2.5 bg-white">
                <Search size={15} className="text-gray-400 mr-2" />

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Session/status..."
                  className="outline-none text-sm w-full"
                />
              </div>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleFetchPayments}
                disabled={paymentsLoading}
                className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white rounded-xl px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2"
              >
                <RefreshCcw size={15} />
                {paymentsLoading ? "Fetching..." : "Fetch Payments"}
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <div className="bg-green-50 border border-green-100 rounded-xl p-3">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <MonitorCog size={14} />
                Selected Terminal
              </p>

              <p className="text-sm font-semibold text-gray-800 mt-1">
                {selectedTerminal?.terminal_name || "-"}{" "}
                <span className="text-gray-400">
                  #{selectedTerminal?.pk_terminal_id || "-"}
                </span>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <WalletCards size={14} />
                Selected Drawer
              </p>

              <p className="text-sm font-semibold text-gray-800 mt-1">
                {selectedDrawer?.drawer_name || "-"}{" "}
                <span className="text-gray-400">
                  #{selectedDrawer?.pk_drawer_id || "-"}
                </span>
              </p>
            </div>
          </div>
        </div>
        </div>

        {/* MAIN CONTENT */}
    <div
  className="bg-white border rounded-xl shadow-sm overflow-hidden flex-1 min-h-0"
  style={{ height: "calc(80vh - 320px)" }}
>
          <div className="grid grid-cols-1 xl:grid-cols-12 h-full min-h-0">
            {/* SESSION TABLE */}
            <div className="xl:col-span-8 border-r flex flex-col min-h-0">
              <div className="px-5 py-4 border-b flex items-center justify-between flex-shrink-0">
                <div>
                  <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                    <ReceiptText size={18} className="text-green-600" />
                    Drawer Sessions
                  </h2>

                  <p className="text-xs text-gray-500">
                    Click View Ledger to see session ledger details.
                  </p>
                </div>
              </div>

              {paymentsLoading ? (
                <div className="py-14 flex justify-center">
                  <LoadingScreen text="Loading payment sessions..." />
                </div>
              ) : (
              <div
  className="flex-1 min-h-0 overflow-y-auto overflow-x-auto"
>
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-green-100 text-gray-600 text-xs uppercase">
                      <tr>
                        <th className="p-4 text-left">Session</th>
                        <th className="p-4 text-left">Opening</th>
                        <th className="p-4 text-left">Closing</th>
                        <th className="p-4 text-left">Expected</th>
                        <th className="p-4 text-left">Difference</th>
                        <th className="p-4 text-left">Cash Flow</th>
                        <th className="p-4 text-left">Status</th>
                        <th className="p-4 text-left">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredSessions?.length === 0 ? (
                        <tr>
                          <td
                            colSpan="8"
                            className="text-center py-12 text-gray-400"
                          >
                            No payment sessions found
                          </td>
                        </tr>
                      ) : (
                        filteredSessions?.map((session) => (
                          <tr
                            key={session.pk_session_id}
                            className={`border-t hover:bg-gray-50 ${
                              String(selectedSession?.pk_session_id) ===
                              String(session.pk_session_id)
                                ? "bg-green-50"
                                : ""
                            }`}
                          >
                            <td className="p-4">
                              <p className="font-semibold text-gray-800">
                                {session.session_code || "-"}
                              </p>

                              <p className="text-xs text-gray-500">
                                Open: {formatDateTime(session.opening_datetime)}
                              </p>

                              <p className="text-xs text-gray-500">
                                Close: {formatDateTime(session.closing_datetime)}
                              </p>
                            </td>

                            <td className="p-4 text-gray-700">
                              {formatCurrency(session.opening_cash)}
                            </td>

                            <td className="p-4 text-gray-700">
                              {formatCurrency(session.closing_cash)}
                            </td>

                            <td className="p-4 text-gray-700">
                              {formatCurrency(session.expected_cash)}
                            </td>

                            <td className="p-4">
                              <span
                                className={`font-semibold ${
                                  Number(session.cash_difference || 0) === 0
                                    ? "text-gray-700"
                                    : Number(session.cash_difference || 0) > 0
                                    ? "text-orange-600"
                                    : "text-red-600"
                                }`}
                              >
                                {formatCurrency(session.cash_difference)}
                              </span>
                            </td>

                            <td className="p-4">
                              <div className="space-y-1 text-xs">
                                <p className="text-green-700">
                                  In: {formatCurrency(session.total_cash_in)}
                                </p>

                                <p className="text-red-600">
                                  Out: {formatCurrency(session.total_cash_out)}
                                </p>

                                <p className="text-gray-600">
                                  Refund: {formatCurrency(session.total_refund)}
                                </p>
                              </div>
                            </td>

                            <td className="p-4">
                              {String(session.session_status).toUpperCase() ===
                              "OPEN" ? (
                                <span className="px-3 py-1 rounded-full text-xs bg-green-100 text-green-700 border border-green-200">
                                  Open
                                </span>
                              ) : (
                                <span className="px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border">
                                  Closed
                                </span>
                              )}
                            </td>

                            <td className="p-4">
                              <button
                                onClick={() => handleViewLedger(session)}
                                className="px-3 py-2 rounded-lg border text-blue-600 hover:bg-blue-50 flex items-center gap-1 text-xs"
                              >
                                <Eye size={14} />
                                View Ledger
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* LEDGER PANEL */}
<div className="xl:col-span-4 bg-slate-50 flex flex-col min-h-0">
  <div className="px-4 py-3 border-b bg-white flex-shrink-0">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="font-semibold  text-gray-800 flex items-center gap-2">
          <Landmark size={17} className="text-blue-600" />
          Session Ledger
        </h2>

        <p className="text-[11px] text-gray-500 mt-0.5">
          {selectedSession
            ? selectedSession.session_code
            : "Select a session to view ledger"}
        </p>
      </div>

      {selectedSession && (
        <span
          className={`px-2.5 py-1 rounded-full text-[11px] border ${
            String(selectedSession.session_status).toUpperCase() === "OPEN"
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-gray-50 text-gray-600 border-gray-200"
          }`}
        >
          {selectedSession.session_status || "-"}
        </span>
      )}
    </div>

    {selectedSession && (
      <>
        {/* OPENING / CLOSING BALANCE */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="rounded-lg border bg-green-50 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">
              Opening Balance
            </p>
            <p className="text-sm font-semibold text-green-700 mt-0.5">
              {formatCurrency(selectedSession.opening_cash)}
            </p>
          </div>

          <div className="rounded-lg border bg-blue-50 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">
              Closing Balance
            </p>
            <p className="text-sm font-semibold text-blue-700 mt-0.5">
              {formatCurrency(selectedSession.closing_cash)}
            </p>
          </div>
        </div>

        {/* SMALL LEDGER SEARCH */}
        <div className="mt-3 flex items-center border rounded-lg px-2.5 py-2 bg-white">
          <Search size={13} className="text-gray-400 mr-2" />

          <input
            value={ledgerSearch}
            onChange={(e) => setLedgerSearch(e.target.value)}
            placeholder="Search ledger..."
            className="outline-none text-xs w-full bg-transparent"
          />
        </div>
      </>
    )}
  </div>

  {ledgerLoading ? (
    <div className="py-14 flex justify-center bg-white">
      <LoadingScreen text="Loading ledger..." />
    </div>
  ) : (
    <div
  className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2"
>
      {!selectedSession ? (
        <div className="border border-dashed rounded-xl p-6 text-center text-gray-400 bg-white">
          <CreditCard className="mx-auto mb-2" size={26} />
          <p className="text-sm">Select any drawer session from the left table.</p>
        </div>
      ) : filteredLedger.length === 0 ? (
        <div className="border border-dashed rounded-xl p-6 text-center text-gray-400 bg-white">
          <p className="text-sm">No ledger entries found</p>
        </div>
      ) : (
        filteredLedger.map((ledger) => (
          <div
            key={ledger.pk_ledger_id}
            className="rounded-xl border bg-white px-3 py-2.5 hover:shadow-sm transition"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {ledger.transaction_type || "-"}
                  </p>

                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] border ${
                      String(ledger.entry_mode).toUpperCase() === "CREDIT"
                        ? "bg-green-50 text-green-700 border-green-100"
                        : "bg-red-50 text-red-600 border-red-100"
                    }`}
                  >
                    {ledger.entry_mode || "-"}
                  </span>
                </div>

                <p className="text-[11px] text-gray-400 mt-0.5">
                  {formatDateTime(ledger.transaction_datetime)}
                </p>
              </div>

              <div className="text-right shrink-0">
                <p
                  className={`text-sm font-semibold ${
                    String(ledger.entry_mode).toUpperCase() === "CREDIT"
                      ? "text-green-700"
                      : "text-red-600"
                  }`}
                >
                  {formatCurrency(ledger.amount)}
                </p>

                <p className="text-[10px] text-gray-400">
                  Bal: {formatCurrency(ledger.balance_after)}
                </p>
              </div>
            </div>

            {(ledger.reference_type || ledger.reference_id) && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
                <span className="px-2 py-0.5 rounded-md bg-gray-50 border">
                  Ref: {ledger.reference_type || "-"}
                </span>

                <span className="px-2 py-0.5 rounded-md bg-gray-50 border">
                  #{ledger.reference_id || 0}
                </span>
              </div>
            )}

            {ledger.remarks && (
              <p className="mt-2 text-[11px] text-gray-500 bg-yellow-50 border border-yellow-100 rounded-lg px-2 py-1.5 line-clamp-2">
                {ledger.remarks}
              </p>
            )}
          </div>
        ))
      )}
    </div>
  )}
</div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Payments;